import React from 'react';
import {
    CompetitiveRoundSummariesDeathEventInner,
    Maps
} from "../../reckon-api";
import Box from "@mui/material/Box";
import {Circle, ImageOverlay, MapContainer, Popup} from "react-leaflet";
import {CRS} from "leaflet";
import {useEffect, useRef, useState} from "react";
import 'leaflet/dist/leaflet.css';
import "./Map.css";
import {CircularProgress, Paper, Stack, Typography} from "@mui/material";
import {attacker_color} from "../../theme";
import {formatDuration} from "../../utils";

export function positionToMapBounds(position: {x:number, y:number}, map: Maps){
    return {
        y: (position.y - map.min_y) / (map.max_y - map.min_y),
        x: (position.x - map.min_x) / (map.max_x - map.min_x),
    }
}

// KillEventWithLocation extends the base death event with location data for heatmap rendering
export interface KillEventWithLocation extends CompetitiveRoundSummariesDeathEventInner {
    victim_location?: {
        position?: { x: number | null; y: number | null; z?: number | null };
    };
}

export function HeatmapKillsRender(props: { map: Maps;
    id: string; children?: React.ReactNode;
    kills: KillEventWithLocation[]
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState(null as number | null);

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    if (entry.contentRect) {
                        const size = Math.floor(Math.min(entry.contentRect.width, entry.contentRect.height));
                        setContainerSize(size+1);
                    }
                }
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    const map_url = `https://res.cloudinary.com/xenesis/image/upload/c_scale,h_${containerSize},w_${containerSize}/valAssets/${props.map ? props.map.id : props.id}.png`;
    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {containerSize ? <MapContainer
                center={[containerSize / 2, containerSize / 2]}
                scrollWheelZoom={false}
                zoom={0}
                dragging={false}
                minZoom={0}
                maxZoom={0}
                attributionControl={false}
                zoomControl={false}
                doubleClickZoom={false}
                crs={CRS.Simple}
                bounds={[
                    [0, 0],
                    [containerSize, containerSize],
                ]}
            >
                <ImageOverlay
                    url={map_url}
                    bounds={[
                        [0, 0],
                        [containerSize, containerSize],
                    ]}
                />
                {
                    props.kills.map((kill, idx) => {
                        const victimPos = kill.victim_location?.position;
                        if (!victimPos || victimPos.x === null || victimPos.y === null) return null;
                        const position_normalized = positionToMapBounds({x: victimPos.x, y: victimPos.y}, props.map);
                        position_normalized.x = position_normalized.x * containerSize;
                        position_normalized.y = position_normalized.y * containerSize;

                        return (
                            <Circle
                                radius={5}
                                key={idx}
                                center={[position_normalized.x, position_normalized.y]}
                                pathOptions={{ color: attacker_color, fillColor: attacker_color, fillOpacity: 0.5 }}
                            >
                                <Popup autoPan={false}>
                                    <Paper
                                        sx={{
                                            alignItems: "left",
                                            justifyContent: "left",
                                            width: "200px",
                                            flexDirection: "column",
                                            display: "flex",
                                            height: 40,
                                            zIndex: 1000,
                                        }}
                                    >
                                        <Stack
                                            direction={"row"}
                                            sx={{
                                                alignItems: "left",
                                                justifyContent: "left",
                                                width: "100%",
                                                display: "flex",
                                            }}
                                            spacing={1}
                                        >
                                            <Typography align="center" sx={{
                                                alignItems: "left",
                                                justifyContent: "left",
                                            }}>{kill.victim_agent}</Typography>
                                            <Typography align="center" sx={{
                                                alignItems: "left",
                                                justifyContent: "left",
                                            }}>{formatDuration(kill.time_in_round)}</Typography>
                                        </Stack>
                                    </Paper>
                                </Popup>
                            </Circle>
                        );
                    })
                }

            </MapContainer> : <CircularProgress/>}
        </Box>
    );
}
